import {
  type InfiniteScrollResponse,
  useInfiniteScroll,
  useLazyGetDemoSearchLinksResultsQuery,
} from '@carboncopyco/data';
import type {
  DemoShare,
  IDemo,
  IPresentation,
  NotesInDesktopMessageProps,
  Play,
  Workspace,
  WorkspaceFolder,
} from '@carboncopyco/types';
import { useBoolean } from 'ahooks';
import constate from 'constate';
import { useState } from 'react';
import type { GoToDemoPreviewArgs } from '../../PresentationModals';

export type DemoPageProps = {
  rootPath: string;
  updateDemo: () => void;
  goToDemoPreview: (options?: GoToDemoPreviewArgs) => void;
  handleCreatePresentation?: () => void;
  goToPresentation: (presentation: IPresentation) => void;
  goToEditor: (url?: string) => void;
  handleCreateTour: () => void;
  goBack: () => void;
  handleShareSettings: () => void;
  organizationId: string;
  demoId: string;
  goToDuplicatedDemo: (demo: IDemo) => void;
  goToCopiedDemo: (demoId: number, workspaceId: Workspace['id']) => void;
  clonePath: string;
  goToWorkspacePage: () => void;
  workspaceId: string;
  goToLinksTab: () => void;
  open?: boolean;
  onOpenChange?: () => void;
  onLearnMore?: () => void;
  onViewShares?: () => void;
  playPresentation?: (play: Play) => void;
  desktopDomain?: string;
  goToFolderPage?: (
    folderId: WorkspaceFolder['id'],
    workspaceId: Workspace['id']
  ) => void;
  readOnly: boolean;
};

export type DemoPageState = DemoPageProps &
  NotesInDesktopMessageProps & {
    noteValue: string;
    setNoteValue: (value: string) => void;
    pendingNavigationRoute: string;
    setPendingNavigationRoute: (route: string) => void;
    demoLinksResponse: InfiniteScrollResponse<DemoShare>;
  };

const useDemoPage = (props: DemoPageProps): DemoPageState => {
  const [noteValue, setNoteValue] = useState('');
  const [pendingNavigationRoute, setPendingNavigationRoute] =
    useState<string>();
  const [showNotesInDesktopMessage, setShowNotesInDesktopMessage] =
    useBoolean(true);

  const demoLinksResponse = useInfiniteScroll(
    useLazyGetDemoSearchLinksResultsQuery
  );

  return {
    ...props,
    noteValue,
    setNoteValue,
    showNotesInDesktopMessage,
    pendingNavigationRoute,
    setPendingNavigationRoute,
    onCloseNotesInDesktopMessage: setShowNotesInDesktopMessage.setFalse,
    demoLinksResponse,
  };
};

export const [DemoPageProvider, useDemoPageProps, useDemoLinks] = constate(
  useDemoPage,
  (value) => value,
  ({ demoLinksResponse }) => demoLinksResponse
);
